import type { PageOptions, SortConfig } from 'src/services/api.types';
import type { LinkedOrgCandidate } from 'src/services/candidates/candidates.types';
import type { ICompanyProfile, ContactLinkType } from 'src/services/connections/connections.types';

import { TenantType } from 'src/types/enums';

import { EntityLinkType } from 'src/services/common.types';
import { DocumentFile, DocumentType } from 'src/services/files/file.types';
import type { User } from '../organisation/organisation.types';
import type { AttachedThread } from '../threads/threads.types';
import { UpdateCustomFieldValueDto } from '../custom-fields/custom-field.types';
import { LinkType } from '../common/common.types';

export interface ContactProfileLink {
  type: LinkType;
  url: string;
}

export type AddContactProps = {
  org_id: string;
  manual_contact?: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    company_name?: string;
    company_profile_id?: string;
    type: ContactType;
    custom_fields?: UpdateCustomFieldValueDto;
  }
  account?: string;
  source: ContactSource
};

export type UpdateContactProps = {
  contact_id: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  special_information?: string;
  links?: ContactProfileLink[];
  linked_candidate_profile_id?: string;
  custom_fields?: UpdateCustomFieldValueDto;
}

export type BulkContactImportProps = {
  organization_id?: string;
  contacts: {
    company_name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    special_information?: string;
    phone_number?: string;
    company_id?: string;
    owner_id?: string;
  }[]
}


export interface IContactCompanyLink {
  id: string;
  type: ContactLinkType;
  company_profile: ICompanyProfile;
}

export interface ICreateContactDocumentDto {
  file_id: string;
  name: string;
  type: DocumentType;
}

export interface GetOrgContactsQuery {
  org_id: string;
  page: PageOptions;
  search_query?: string;
  contact_type?: TenantType;
  linked_contacts?: boolean;
  sort_config?: SortConfig;
}

export interface RemoveContactProps {
  contact_id: string;
}

export type Contact = {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  type: TenantType;
  contact_type: ContactType;
  email: string;
  status: string;
  source: ContactSource;
  special_information: string;
  company_name: string;
  linked_candidate: LinkedOrgCandidate | null;
  thread: AttachedThread;
  account_owner?: Partial<User>;
  links?: ContactProfileLink[];
  account?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    organization: {
      company_name: string;
      company_information: string;
      company_type: string;
    }
  }
  created_at: '2024-07-16T22:44:56.000Z';
};

export type ContactDocument = {
  id: string;
  document: DocumentFile;
  created_at: Date;
}

export enum ContactSource {
  JOB_INTERACTION = 'job_interaction',
  MANUAL_LINK = 'manual_link',
}

export enum ContactType {
  HIRING_MANAGER = 'hiring_manager', // default client type
  DECISION_MAKER = 'decision_maker',
  INTERNAL_TALENT = 'internal_talent',
  EXECUTIVE = 'executive',
  ADVISOR = 'advisor',
  LEGAL = 'legal',
  HR = 'hr',

  OBSERVER = 'observer',

  CONSULTANT = 'consultant', // default recruiter type
  ACCOUNT_MANAGER = 'account_manager',
  TEAM_MANAGER = 'team_manager',
  RESOURCER = 'resourcer',
  ADMINISTRATION = 'administration',
  PROSPECT = 'prospect',

  CANDIDATE = 'candidate', // default candidate type

  OTHER = 'other'
}

export enum ContactLinkEntityType {
  CONTACT = 'contact',
  COMPANY_PROFILE = 'company_profile',
  DEAL = 'deal',
  MEETING = 'meeting'
}

export interface AddContactLinkProps {

  entity_id: string;

  entity_type: ContactLinkEntityType;

  contacts: {
    id: string;
    type: EntityLinkType;
  }[];
}
